import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import './MyScripts.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function MyScripts({ user }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [searchType, setSearchType] = useState('transaction_id');
  const [searchValue, setSearchValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [assigningRoles, setAssigningRoles] = useState(false);
  const [rolesRequested, setRolesRequested] = useState(false);
  const [totalSpent, setTotalSpent] = useState(0);
  const toast = useToast();
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      fetchScripts();
      hasFetched.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchScripts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/scripts/my-scripts`, {
        withCredentials: true
      });
      if (response.data.scripts) {
        setScripts(response.data.scripts);
        setTotalSpent(response.data.totalSpent || 0);
      } else {
        setScripts(response.data);
      }
    } catch (error) {
      console.error('Error fetching scripts:', error);
      toast.error('Failed to load scripts');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!searchValue.trim()) {
      toast.warning('Please enter a value');
      return;
    }

    setVerifying(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/scripts/verify`,
        { searchValue: searchValue.trim(), searchType },
        { withCredentials: true }
      );

      toast.success(`Successfully verified ${response.data.scripts.length} script(s)!`);
      setShowVerifyModal(false);
      setSearchValue('');
      fetchScripts();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Verification failed';
      const errorMessage = error.response?.data?.message;
      const claimedBy = error.response?.data?.claimedBy;
      const declined = error.response?.data?.declined;
      
      if (declined) {
        toast.error(errorMessage || 'This payment was declined. Please try purchasing again.');
      } else if (claimedBy) {
        toast.error(`Transaction already claimed by ${claimedBy}`);
      } else if (errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleAssignRoles = async () => {
    setAssigningRoles(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/scripts/assign-roles`,
        {},
        { withCredentials: true }
      );

      if (response.data.assigned && response.data.assigned.length > 0) {
        toast.success(`Assigned ${response.data.assigned.length} Discord role(s)!`);
        setRolesRequested(true);
      } else {
        toast.info(response.data.message || 'No new roles to assign');
      }

      fetchScripts();
    } catch (error) {
      if (error.response?.status === 429) {
        toast.warning(error.response.data.message || 'Please wait 24 hours between role requests');
      } else {
        toast.error(error.response?.data?.error || 'Failed to assign roles');
      }
    } finally {
      setAssigningRoles(false);
    }
  };

  const groupedScripts = scripts.reduce((acc, script) => {
    const status = script.roles_assigned ? 'assigned' : 'pending';
    if (!acc[status]) acc[status] = [];
    acc[status].push(script);
    return acc;
  }, {});

  if (loading) {
    return <div className="loading">Loading your scripts...</div>;
  }

  return (
    <div className="my-scripts">
      <div className="panel-header">
        <h1>My Scripts</h1>
        <div className="header-actions">
          <button 
            onClick={() => setShowVerifyModal(true)} 
            className="btn btn-primary"
          >
            <span className="emoji">🔍</span> Verify Purchase
          </button>
          <button
            onClick={handleAssignRoles}
            className="btn btn-success"
            disabled={assigningRoles || rolesRequested}
          >
            <span className="emoji">{rolesRequested ? '🔒' : '🎭'}</span> {assigningRoles ? 'Assigning...' : rolesRequested ? 'Roles Requested' : 'Get Roles'}
          </button>
        </div>
      </div>

      {scripts.length === 0 ? (
        <div className="empty-state glass">
          <div className="empty-icon"><span className="emoji">📦</span></div>
          <h3>No Scripts Found</h3>
          <p>Verify your purchase to see your scripts here</p>
          <button onClick={() => setShowVerifyModal(true)} className="btn btn-primary">
            Verify Purchase
          </button>
        </div>
      ) : (
        <>
          <div className="scripts-stats glass">
            <div className="stat-card">
              <div className="stat-number">{scripts.length}</div>
              <div className="stat-label">Total Scripts</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {totalSpent && typeof totalSpent === 'number' && totalSpent > 0 
                  ? `$${totalSpent.toFixed(2)}` 
                  : '$0.00'}
              </div>
              <div className="stat-label">Total Spent</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{groupedScripts.pending?.length || 0}</div>
              <div className="stat-label">Pending Roles</div>
            </div>
          </div>

          <div className="scripts-grid">
            {scripts.map((script) => (
              <div key={script.id} className="script-card glass">
                <div className="script-header">
                  <h3 className="script-name">{script.script_name}</h3>
                </div>
                
                <div className="script-details">
                  {script.discord_role_name && (
                    <div className="detail-item">
                      <span className="detail-label"><span className="emoji">🎭</span> Discord Role:</span>
                      <span className="detail-value">{script.discord_role_name}</span>
                    </div>
                  )}
                  
                  {script.cfx_name && (
                    <div className="detail-item">
                      <span className="detail-label"><span className="emoji">👤</span> CFX Name:</span>
                      <span className="detail-value">{script.cfx_name}</span>
                    </div>
                  )}
                  
                  <div className="detail-item">
                    <span className="detail-label"><span className="emoji">🔖</span> Transaction ID:</span>
                    <span className="detail-value transaction-id">{script.transaction_id}</span>
                  </div>

                  {script.total_price && (
                    <div className="detail-item">
                      <span className="detail-label"><span className="emoji">💰</span> Price:</span>
                      <span className="detail-value">{script.total_price} {script.currency}</span>
                    </div>
                  )}
                  
                  <div className="detail-item">
                    <span className="detail-label"><span className="emoji">📅</span> Verified:</span>
                    <span className="detail-value">
                      {new Date(script.verified_at).toLocaleDateString()}
                    </span>
                  </div>

                  {script.roles_assigned_at && (
                    <div className="detail-item">
                      <span className="detail-label"><span className="emoji">🎉</span> Roles Assigned:</span>
                      <span className="detail-value">
                        {new Date(script.roles_assigned_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showVerifyModal && (
        <div className="modal-backdrop" onClick={() => setShowVerifyModal(false)}>
          <div className="verify-modal glass" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Verify Purchase</h2>
              <button className="close-btn" onClick={() => setShowVerifyModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                Enter your Transaction ID or CFX Name from your Tebex purchase to verify your scripts.
              </p>

              <form onSubmit={handleVerify}>
                <div className="form-group">
                  <label>Search By</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="transaction_id"
                        checked={searchType === 'transaction_id'}
                        onChange={(e) => setSearchType(e.target.value)}
                      />
                      <span>Transaction ID</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="cfx_name"
                        checked={searchType === 'cfx_name'}
                        onChange={(e) => setSearchType(e.target.value)}
                      />
                      <span>CFX Name</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    {searchType === 'transaction_id' ? 'Transaction ID' : 'CFX Name'}
                  </label>
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={
                      searchType === 'transaction_id' 
                        ? 'e.g., 92774897'
                        : 'e.g., markmoney'
                    }
                    required
                    className="verify-input"
                  />
                  <small className="input-hint">
                    {searchType === 'transaction_id' 
                      ? 'Find this in your Tebex purchase confirmation email'
                      : 'Your FiveM/CFX username'
                    }
                  </small>
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={verifying}
                  >
                    {verifying ? 'Verifying...' : 'Verify Purchase'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowVerifyModal(false)} 
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyScripts;
